import React from 'react';

import { useExperimentalCalculations } from '../../hooks/useExperimentalCalculations';
import { findMissingExperimentalFields } from '../../utils/experimentalSetups';

const SIZE_SETUP_FIELDS = [
  { key: 'temperature_c', label: 'Temperature (°C)' },
  { key: 'viscosity_mpa_s', label: 'Viscosity (mPa·s)' },
  { key: 'total_length_cm', label: 'Total capillary length (cm)' },
  { key: 'post_detector_length_cm', label: 'Post-detector length (cm)' },
  { key: 'capillary_diam_um', label: 'Capillary diameter (µm)' },
  { key: 'injection_pressure_mbar', label: 'Injection pressure (mbar)' },
  { key: 'injection_time_s', label: 'Injection time (s)' },
  { key: 'mobilizing_pressure_mbar', label: 'Mobilizing pressure (mbar)' },
];

function formatPreview(value, digits = 3) {
  if (value === '' || value === null || value === undefined) return '—';
  return Number(value).toFixed(digits);
}

export function SizeSetupForm({ values, onChange, disabled = false }) {
  const { values: previewValues, warnings: previewWarnings } =
    useExperimentalCalculations(values);
  const showPreview =
    findMissingExperimentalFields(values).length === 0 &&
    previewValues.eff_cap_length_cm !== '';

  return (
    <div className="mt-4 space-y-4 rounded border border-slate-200 bg-slate-50 p-3">
      <div>
        <div className="text-sm font-semibold text-slate-800">
          Experimental setup
        </div>
        <p className="mt-1 text-xs text-slate-500">
          These effective Size conditions are frozen with every uploaded file.
        </p>
        <p className="mt-1 text-xs text-slate-500">
          These setup values apply to all files in this upload. You can change them
          before uploading the next group.
        </p>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        {SIZE_SETUP_FIELDS.map((field) => (
          <label className="block text-xs" key={field.key}>
            <span className="text-slate-600">{field.label}</span>
            <input
              type="number"
              step="any"
              className="mt-1 w-full rounded border border-slate-300 bg-white p-2 text-sm"
              value={values[field.key] ?? ''}
              disabled={disabled}
              onChange={(event) => onChange(field.key, event.target.value)}
            />
          </label>
        ))}
      </div>

      {showPreview && (
        <div className="rounded border border-slate-200 bg-white p-3">
          <div className="text-xs font-semibold text-slate-700">
            Advisory calculated preview
          </div>
          <div className="mt-2 grid gap-2 lg:grid-cols-3">
            <div className="rounded border border-slate-200 p-2 text-xs">
              <div className="text-slate-500">Eff. capillary length (cm)</div>
              <div className="text-slate-900">
                {formatPreview(previewValues.eff_cap_length_cm, 2)}
              </div>
            </div>
            <div className="rounded border border-slate-200 p-2 text-xs">
              <div className="text-slate-500">Capillary volume (nL)</div>
              <div className="text-slate-900">
                {formatPreview(previewValues.capillary_volume_nl, 2)}
              </div>
            </div>
            <div className="rounded border border-slate-200 p-2 text-xs">
              <div className="text-slate-500">Injected volume (nL)</div>
              <div className="text-slate-900">
                {formatPreview(previewValues.injected_volume_nl, 3)}
              </div>
            </div>
            <div className="rounded border border-slate-200 p-2 text-xs">
              <div className="text-slate-500">Injection fraction (%)</div>
              <div className="text-slate-900">
                {formatPreview(previewValues.injection_fraction_pct, 3)}
              </div>
            </div>
            <div className="rounded border border-slate-200 p-2 text-xs">
              <div className="text-slate-500">Predicted t0 (s)</div>
              <div className="text-slate-900">
                {formatPreview(previewValues.predicted_t0_s, 2)}
              </div>
            </div>
          </div>
          {previewWarnings.length > 0 && (
            <ul className="mt-2 list-disc space-y-1 rounded border border-amber-200 bg-amber-50 p-2 pl-6 text-xs text-amber-800">
              {previewWarnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
